// mantine
import {
    Alert,
    Divider,
    Group,
    Loader,
    Text,
    UnstyledButton
} from '@mantine/core';
// components
import { TriangleAlert } from 'lucide-react';
import { TProfile } from '@newo/api/gateway';
import Plans from '@/pages/Profile/components/Billing/components/Plans';
import ActiveSubscription from '@/pages/Profile/components/Billing/components/ActiveSubscription';
import DevelopmentSubscription from '@/pages/Profile/components/Billing/components/DevelopmentSubscription';
import TrialSubscription from '@/pages/Profile/components/Billing/components/TrialSubscription';
import NoSubscription from '@/pages/Profile/components/Billing/components/NoSubscription';
// icons
// utils
import {
    useBillingQuery,
    useBillingStripeUrlMutation
} from '@/services/customer/profile/profile.queries.ts';

interface BillingProps {
    profile: TProfile;
}

const BILLING_PROCESS_GUIDE_URL =
    'https://help.newo.ai/en/articles/13786146-billing-process-guide-for-partners';

const Billing = ({ profile }: BillingProps) => {
    const {
        data: billing,
        isLoading: isBillingLoading,
        isError
    } = useBillingQuery();
    const { mutateAsync: getStripeUrl, isPending: isCancelPending } =
        useBillingStripeUrlMutation();

    const handleSubscriptionCancel = async () => {
        try {
            const response = await getStripeUrl({
                return_url: globalThis.location.href,
                flow_data_type: 'subscription_cancel'
            });
            globalThis.location.href = response.url;
        } catch (error) {
            console.log(error);
        }
    };

    if (isBillingLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="text-red-6">
                    Error loading billing information
                </div>
            </div>
        );
    }

    const subscription = billing?.subscription_info;
    const stage = subscription?.subscription_stage;
    const isDevelopmentMode = stage === 'development';
    const isTrialMode = stage === 'trial';
    const isActiveMode = stage === 'paid';
    const activePlan = billing?.subscription_info?.product_key ?? undefined;

    return (
        <div className="flex flex-col gap-12">
            {profile.status === 'temporal' ? (
                <Alert
                    color="yellow"
                    icon={<TriangleAlert />}
                    title="Temporary Status Notice"
                    variant="light"
                >
                    <Group align="flex-start" w="100%" wrap="nowrap">
                        <Text>
                            Your account is currently in Temporary status.
                            Please select a subscription to continue using our
                            services. If no subscription is chosen, your account
                            will be suspended soon.
                        </Text>
                        <UnstyledButton
                            component="a"
                            href={BILLING_PROCESS_GUIDE_URL}
                            target="_blank"
                            w="fit-content"
                        >
                            <Text
                                className="!text-xs md:!text-base border-b-black border-b whitespace-nowrap"
                                fz={16}
                                w="fit-content"
                            >
                                Learn more
                            </Text>
                        </UnstyledButton>
                    </Group>
                </Alert>
            ) : undefined}
            {subscription ? undefined : <NoSubscription />}
            <Divider />
            {subscription && isDevelopmentMode ? (
                <DevelopmentSubscription
                    data={subscription}
                    isCancelPending={isCancelPending}
                    onCancel={handleSubscriptionCancel}
                />
            ) : undefined}
            {subscription && isTrialMode ? (
                <TrialSubscription
                    data={subscription}
                    unitsSpent={billing.units_spent ?? 0}
                    onCancel={handleSubscriptionCancel}
                />
            ) : undefined}
            {subscription && isActiveMode ? (
                <ActiveSubscription
                    data={subscription}
                    isCancelPending={isCancelPending}
                    unitsSpent={billing.units_spent ?? 0}
                    onCancel={handleSubscriptionCancel}
                />
            ) : undefined}
            <Plans
                activePlan={activePlan}
                idn={profile.idn}
                isDeveloperMode={isDevelopmentMode}
                withTrial={
                    !billing?.checkout_completed || stage === 'development'
                }
            />
        </div>
    );
};

export default Billing;
